import { FormField } from '../../../api/types/form';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Chip,
    FormControl,
    FormHelperText,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { FormFieldProps } from './index';
import { FormValues } from '../../form/types';
import { TemplateFormType } from '../../../utils/cms';
import { useFormErrors } from '../../../contexts/FormContext';
import { useDidMountEffect } from '../../../utils/mount';

type NewCollectionRowProps = {
    row?: Array<FormFieldProps | TemplateFormType>;
    rowKey: number;
    initialValues: FormValues;
    onRemove?: (fieldKey: number) => void;
    onChange?: (fieldKey: number, val: FormValues) => void;
};

type CollectionFieldProps = {
    config: FormField;
    onChange: (val: string) => void;
};

function CollectionField({ config, onChange }: CollectionFieldProps) {
    const { translate } = useTranslation();

    const [value, setValue] = useState<string>(config.value ?? '');

    useEffect(() => {
        if (onChange && value !== config.value) {
            onChange(value);
        }
    }, [value]);

    const changeValue = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value);
    };

    return (
        <FormControl fullWidth>
            <TextField
                name={config.name}
                label={translate(config.label)}
                InputLabelProps={{
                    sx: { lineHeight: 1, mt: 0.5 },
                }}
                value={value ?? ''}
                variant="outlined"
                onChange={changeValue}
                required={config.required}
                type={config.type}
            />
        </FormControl>
    );
}

function NewCollectionRow({
    row,
    rowKey,
    initialValues,
    onRemove = () => {},
    onChange = () => {},
}: NewCollectionRowProps) {
    const [values, setValues] = useState<FormValues>(initialValues);

    useDidMountEffect(() => {
        if (typeof rowKey !== 'undefined') {
            onChange(rowKey, values);
        }
    }, [values]);

    const buildChanges = (key: string, val: any) => {
        if (typeof key !== 'undefined') {
            setValues(draft => {
                const temp = { ...draft };
                temp[key] = val;
                return temp;
            });
        }
    };

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {row?.map((field, index) => (
                <CollectionField
                    key={`collection-field-${index}`}
                    config={{ ...field, value: values[field.name] }}
                    onChange={val => {
                        buildChanges(field.name, val);
                    }}
                />
            ))}
            <IconButton onClick={() => onRemove(rowKey)}>
                <DeleteOutlined />
            </IconButton>
        </Stack>
    );
}

export default function CollectionFormField({
    name,
    label,
    value,
    onChange,
    disabled,
    cssClassName,
    children,
}: FormFieldProps) {
    const { translate } = useTranslation();
    const [rows, setRows] = useState<Array<FormValues>>(value);

    const { error } = useFormErrors(name);

    useDidMountEffect(() => {
        onChange(rows);
    }, [rows]);

    const summarize = (values?: FormValues): string => {
        let response: Array<string | number> = [];
        for (const content of Object.values(values ?? {})) {
            if (typeof content === 'string' || typeof content === 'number') {
                response.push(content);
            }
        }
        return response.join(' | ');
    };

    const addRow = () => {
        setRows(draft => {
            const temp = Array.from(draft ?? []);
            temp.push({});
            return temp;
        });
    };

    const changeValue = (key: number, val: FormValues) => {
        if (typeof key !== 'undefined') {
            setRows(draft => {
                const temp = Array.from(draft);
                temp[key] = val;
                return temp;
            });
        }
    };

    const removeRow = (index: number) => {
        setRows(draft => {
            const temp = Array.from(draft);
            temp.splice(index, 1);
            return temp;
        });
    };

    const renderRows = () => {
        return rows?.map((rowValues, index) => {
            return (
                <NewCollectionRow
                    key={index}
                    row={children}
                    initialValues={rowValues}
                    rowKey={index}
                    onChange={changeValue}
                    onRemove={removeRow}
                />
            );
        });
    };

    if (disabled) return null;

    const renderNormal = () => (
        <Stack spacing={1}>
            <Stack spacing={2}>{renderRows()}</Stack>
            <Button startIcon={<PlusCircleOutlined />} onClick={addRow}>
                {translate('form.general.add')}
            </Button>
        </Stack>
    );

    const renderAccordion = () => (
        <Accordion variant="outlined">
            <AccordionSummary>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    width="100%"
                    flexWrap="wrap">
                    {rows?.map((item, index) => (
                        <Chip
                            key={`chip-${index}`}
                            color="primary"
                            sx={{ borderRadius: 5 }}
                            label={summarize(item)}
                        />
                    ))}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>{renderNormal()}</AccordionDetails>
        </Accordion>
    );

    return (
        <FormControl fullWidth className={cssClassName}>
            <Stack spacing={2} position="relative" width="100%">
                <Typography color="text.secondary">
                    {translate(label)}
                </Typography>
                {rows?.length > 5 ? renderAccordion() : renderNormal()}
                {Boolean(error) && (
                    <FormHelperText error id={name + '-error'}>
                        {translate(error ?? 'error')}
                    </FormHelperText>
                )}
            </Stack>
        </FormControl>
    );
}
